import { inr, dmy, dmyTime, titleize } from "@/lib/format";
import { documentStyle, TemplatedHeader, useActiveTemplate } from "@/components/PrintDoc";

export const TEMPLATES = [
  { value: "classic", label: "Classic", hint: "Traditional bordered tax invoice" },
  { value: "modern", label: "Modern", hint: "Clean, airy layout with a bold summary" },
  { value: "compact", label: "Compact Pharmacy", hint: "Dense counter-style bill" },
];

const Money = ({ label, value, strong = false }) => (
  <div className={`flex justify-between ${strong ? "font-ui text-[14px] font-bold" : ""}`}>
    <span className={strong ? "" : "text-slate-600"}>{label}</span><span>{value}</span>
  </div>
);

const Totals = ({ sale }) => (
  <div className="w-72 space-y-1 text-[12px]">
    <Money label="Taxable amount" value={inr(sale.subtotal)} />
    <Money label="Item discount" value={`- ${inr(sale.item_discount || 0)}`} />
    <Money label="Total tax (GST)" value={inr(sale.gst_total)} />
    {sale.overall_discount > 0 && <Money label="Bill discount" value={`- ${inr(sale.overall_discount)}`} />}
    <Money label="Round off" value={inr(sale.round_off || 0)} />
    <div className="my-1.5 border-t-2 border-slate-800" />
    <Money label="Grand total" value={inr(sale.total)} strong />
    <Money label="Paid" value={inr(sale.paid_amount)} />
    {sale.change > 0 && <Money label="Change returned" value={inr(sale.change)} />}
  </div>
);

const Payments = ({ sale }) => (
  <div className="text-[11px]">
    <p className="font-semibold uppercase tracking-wider text-slate-700">Payment</p>
    {(sale.payments || [{ mode: sale.payment_mode, amount: sale.paid_amount }]).map((p, i) => (
      <p key={i} className="mt-1">
        {titleize(p.mode)} — {inr(p.amount)}{p.reference ? ` · Ref ${p.reference}` : ""}
      </p>
    ))}
  </div>
);

const Terms = ({ sale, business }) => {
  const text = sale.terms || business?.invoice_terms;
  if (!text) return null;
  return (
    <div className="mt-6 text-[10.5px] text-slate-600">
      <p className="font-semibold uppercase tracking-wider text-slate-700">Terms &amp; conditions</p>
      <p className="mt-1 whitespace-pre-wrap">{text}</p>
    </div>
  );
};

const Lines = ({ sale, dense = false }) => (
  <table className={`w-full border-collapse ${dense ? "text-[10.5px]" : "text-[11.5px]"}`}>
    <thead>
      <tr className="border-y-2 border-slate-800 text-left">
        <th className="py-1.5 w-6">#</th>
        <th className="py-1.5">Product</th>
        <th className="py-1.5">Batch</th>
        <th className="py-1.5">Expiry</th>
        <th className="py-1.5 text-right">MRP</th>
        <th className="py-1.5 text-right">Rate</th>
        <th className="py-1.5 text-right">Qty</th>
        <th className="py-1.5 text-right">Disc</th>
        <th className="py-1.5 text-right">GST</th>
        <th className="py-1.5 text-right">Amount</th>
      </tr>
    </thead>
    <tbody>
      {(sale.items || []).map((l, i) => (
        <tr key={i} className="border-b border-slate-200">
          <td className="py-1.5">{i + 1}</td>
          <td className="py-1.5 font-medium">{l.medicine_name}{l.hsn ? <span className="block text-[9.5px] text-slate-500">HSN {l.hsn}</span> : null}</td>
          <td className="py-1.5">{l.batch_no}</td>
          <td className="py-1.5">{l.expiry_date ? dmy(l.expiry_date) : "—"}</td>
          <td className="py-1.5 text-right">{inr(l.mrp)}</td>
          <td className="py-1.5 text-right">{inr(l.rate)}</td>
          <td className="py-1.5 text-right">{l.quantity}</td>
          <td className="py-1.5 text-right">{inr(l.discount || 0)}</td>
          <td className="py-1.5 text-right">{l.gst_percent}%</td>
          <td className="py-1.5 text-right font-medium">{inr(l.line_total)}</td>
        </tr>
      ))}
    </tbody>
  </table>
);

const Meta = (sale) => [
  ["Invoice no.", sale.invoice_no],
  ["Invoice date", dmy(sale.invoice_date || sale.created_at)],
  ["Due date", sale.due_date ? dmy(sale.due_date) : "—"],
  ["Customer", sale.customer_name],
  ["Mobile", sale.customer_phone || "—"],
  ["Doctor / reference", sale.doctor_name || "—"],
];

/** Three admin-selectable print templates, all driven by the same server-computed sale. */
export function PharmacyInvoiceDoc({ sale, business, template = "classic" }) {
  const documentTemplate = useActiveTemplate("pharmacy", "pharmacy_invoice");
  if (!sale) return null;

  if (template === "modern") {
    return (
      <div style={documentStyle(documentTemplate)}>
        <div className="flex items-start justify-between gap-6">
          <div className="flex items-start gap-4">
            {business?.logo_data && <img src={business.logo_data} alt="" className="h-14 w-14 object-contain" />}
            <div>
              <p className="font-ui text-[22px] font-extrabold tracking-tight">{business?.name}</p>
            <p className="text-[11px] text-slate-600">{[business?.address, business?.city, business?.state, business?.pincode].filter(Boolean).join(", ")}</p>
            <p className="text-[11px] text-slate-600">{[business?.phone, business?.email].filter(Boolean).join(" · ")}</p>
              <p className="text-[11px] text-slate-600">{[business?.gstin && `GSTIN ${business.gstin}`, business?.drug_licence && `DL ${business.drug_licence}`].filter(Boolean).join(" · ")}</p>
            </div>
          </div>
          <div className="rounded-lg bg-slate-900 px-5 py-4 text-right text-white">
            <p className="text-[9.5px] uppercase tracking-[0.2em] text-white/60">Tax invoice</p>
            <p className="font-ui text-[16px] font-bold">{sale.invoice_no}</p>
            <p className="text-[10.5px] text-white/70">{dmyTime(sale.invoice_date || sale.created_at)}</p>
          </div>        </div>
        <div className="mt-7 grid grid-cols-3 gap-x-6 gap-y-3">
          {Meta(sale).slice(3).map(([k, v]) => (
            <div key={k}><p className="text-[9.5px] uppercase tracking-wider text-slate-500">{k}</p><p className="font-medium">{v}</p></div>
          ))}
          <div><p className="text-[9.5px] uppercase tracking-wider text-slate-500">Cashier</p><p className="font-medium">{sale.cashier || "—"}</p></div>
        </div>
        <div className="mt-7"><Lines sale={sale} /></div>
        <div className="mt-6 flex flex-wrap items-start justify-between gap-8">
          <Payments sale={sale} />
          <Totals sale={sale} />
        </div>
        <Terms sale={sale} business={business} />
        <p className="mt-10 text-center text-[10px] text-slate-500">Thank you for choosing {business?.name}. Get well soon.</p>
      </div>
    );
  }

  if (template === "compact") {
    return (
      <div className="mx-auto max-w-[520px]" style={documentStyle(documentTemplate)}>
        <div className="text-center">
          {business?.logo_data && <img src={business.logo_data} alt="" className="mx-auto mb-2 h-12 w-12 object-contain" />}
          <p className="font-ui text-[16px] font-extrabold uppercase tracking-tight">{business?.name}</p>
          <p className="text-[10px] text-slate-600">{[business?.address, business?.city].filter(Boolean).join(", ")}</p>
          <p className="text-[10px] text-slate-600">{business?.phone} {business?.drug_licence ? `· DL ${business.drug_licence}` : ""}</p>
          <p className="mt-2 border-y border-dashed border-slate-400 py-1 text-[10.5px] font-semibold uppercase tracking-wider">Cash / credit memo</p>
        </div>
        <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1 text-[10.5px]">
          {Meta(sale).map(([k, v]) => (
            <p key={k}><span className="text-slate-500">{k}: </span><b>{v}</b></p>
          ))}
        </div>
        <div className="mt-3"><Lines sale={sale} dense /></div>
        <div className="mt-3 flex justify-end"><Totals sale={sale} /></div>
        <div className="mt-3 border-t border-dashed border-slate-400 pt-2"><Payments sale={sale} /></div>
        <Terms sale={sale} business={business} />
        <p className="mt-6 text-center text-[10px] text-slate-500">
          Medicines are not returnable unless supplied in error. Keep this bill for warranty and insurance.
        </p>
      </div>
    );
  }

  return (
    <div style={documentStyle(documentTemplate)}>
      <TemplatedHeader template={documentTemplate} business={business} docTitle="Tax Invoice"
        right={<>
          <p className="text-[11px]">Invoice: <b>{sale.invoice_no}</b></p>
          <p className="text-[11px]">{dmyTime(sale.invoice_date || sale.created_at)}</p>
          {business?.drug_licence && <p className="text-[11px]">DL: {business.drug_licence}</p>}
        </>} />
      <div className="mt-4 grid grid-cols-3 gap-x-6 gap-y-2">
        {Meta(sale).map(([k, v]) => (
          <div key={k}>
            <p className="text-[9.5px] uppercase tracking-wider text-slate-500">{k}</p>
            <p className="font-medium">{v}</p>
          </div>
        ))}
      </div>
      <div className="mt-6"><Lines sale={sale} /></div>
      <div className="mt-5 flex flex-wrap items-start justify-between gap-8">
        <Payments sale={sale} />
        <Totals sale={sale} />
      </div>
      <Terms sale={sale} business={business} />
      <div className="mt-10 flex items-end justify-between">
        <p className="text-[10px] text-slate-500">Billed by {sale.cashier || "—"}</p>
        <p className="w-56 border-t border-slate-800 pt-1 text-center text-[10.5px]">Authorised signatory</p>
      </div>
    </div>
  );
}
